import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PortalNavbar from './PortalNavbar';
import '../design-system.css';

const UserManager = () => {
    const { setAuthToken } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newUser, setNewUser] = useState({ username: '', password: '', role: 'user' });
    const [editingUser, setEditingUser] = useState(null);
    const [editForm, setEditForm] = useState({ password: '', role: '', is_active: true });
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await axios.get('/api/users');
            setUsers(response.data);
        } catch (error) {
            console.error("Error fetching users", error);
            if (error.response && error.response.status === 403) {
                alert("Accesso negato. Solo gli admin possono vedere questa pagina.");
                navigate('/');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        setError('');
        try {
            await axios.post('/api/users', newUser);
            setNewUser({ username: '', password: '', role: 'user' });
            fetchUsers();
        } catch (error) {
            console.error("Error creating user", error);
            setError(error.response?.data?.detail || "Errore durante la creazione dell'utente");
        }
    };

    const handleDeleteUser = async (userId) => {
        if (!window.confirm("Sei sicuro di voler eliminare questo utente?")) return;
        try {
            await axios.delete(`/api/users/${userId}`);
            fetchUsers();
        } catch (error) {
            console.error("Error deleting user", error);
            alert(error.response?.data?.detail || "Errore durante l'eliminazione");
        }
    };

    const handleImpersonate = async (username) => {
        if (!window.confirm(`Sei sicuro di voler impersonare ${username}?`)) return;
        try {
            const response = await axios.post('/api/auth/impersonate', { username });
            const { access_token } = response.data;
            setAuthToken(access_token);
            alert(`Ora stai impersonando ${username}. Verrai reindirizzato alla chat.`);
            navigate('/chat');
        } catch (error) {
            console.error("Error impersonating user", error);
            alert(error.response?.data?.detail || "Errore durante l'impersonificazione");
        }
    };

    const startEditing = (user) => {
        setEditingUser(user);
        setEditForm({ password: '', role: user.role, is_active: user.is_active });
    };

    const cancelEditing = () => {
        setEditingUser(null);
        setEditForm({ password: '', role: '', is_active: true });
    };

    const handleUpdateUser = async (e) => {
        e.preventDefault();
        try {
            const payload = {};
            if (editForm.password) payload.password = editForm.password;
            if (editForm.role !== editingUser.role) payload.role = editForm.role;
            if (editForm.is_active !== editingUser.is_active) payload.is_active = editForm.is_active;

            await axios.put(`/api/users/${editingUser.id}`, payload);
            setEditingUser(null);
            fetchUsers();
        } catch (error) {
            console.error("Error updating user", error);
            alert(error.response?.data?.detail || "Errore durante l'aggiornamento");
        }
    };

    // Filtra utenti
    const filteredUsers = users.filter(u =>
        u.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.role.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const roleBadge = (role) => {
        const map = { admin: 'ds-badge-gray', editor: 'ds-badge-amber', viewer: 'ds-badge-blue', user: 'ds-badge-green',
            hr: 'ds-badge-blue', sales: 'ds-badge-amber', management: 'ds-badge-gray' };
        return <span className={`ds-badge ${map[role] || 'ds-badge-gray'}`}>{role}</span>;
    };

    return (
        <div className="ds-page">
            <PortalNavbar section="Gestione Utenti" showLogout />
            <main className="ds-page-main">
                <h1 className="ds-page-title">Gestione Utenti</h1>
                <p className="ds-page-subtitle">Account, ruoli e permessi</p>

                {/* Ricerca */}
                <div className="ds-toolbar">
                    <input
                        type="text"
                        className="ds-input ds-search"
                        placeholder="Cerca utente o ruolo..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                {/* Form creazione */}
                <div className="ds-panel" style={{ marginBottom: '24px' }}>
                    <h3 className="ds-panel-title">Aggiungi Nuovo Utente</h3>
                    {error && <div className="ds-badge ds-badge-red" style={{ marginBottom: '12px' }}>{error}</div>}
                    <form onSubmit={handleCreateUser} className="form-responsive" style={{ display: 'flex', gap: '14px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: '160px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', color: 'var(--ds-txt-2)' }}>Username</label>
                            <input type="text" className="ds-input" value={newUser.username}
                                onChange={(e) => setNewUser({ ...newUser, username: e.target.value })} required />
                        </div>
                        <div style={{ flex: 1, minWidth: '160px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', color: 'var(--ds-txt-2)' }}>Password</label>
                            <input type="password" className="ds-input" value={newUser.password}
                                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} required />
                        </div>
                        <div style={{ flex: 1, minWidth: '160px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', color: 'var(--ds-txt-2)' }}>Ruolo</label>
                            <select className="ds-select" value={newUser.role}
                                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}>
                                <option value="user">User</option>
                                <option value="admin">Admin</option>
                                <option value="editor">Editor</option>
                                <option value="viewer">Viewer</option>
                                <option value="hr">HR</option>
                                <option value="sales">Sales</option>
                                <option value="management">Management</option>
                            </select>
                        </div>
                        <button type="submit" className="ds-btn ds-btn-primary">Crea Utente</button>
                    </form>
                </div>

                {/* Modal modifica */}
                {editingUser && (
                    <div className="modal-overlay">
                        <div className="modal-content">
                            <h3 style={{ marginTop: 0, marginBottom: '20px', fontSize: '16px' }}>Modifica Utente: {editingUser.username}</h3>
                            <form onSubmit={handleUpdateUser}>
                                <div className="form-group">
                                    <label>Nuova Password (lascia vuoto per non cambiare)</label>
                                    <input type="password" className="ds-input" value={editForm.password}
                                        onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label>Ruolo</label>
                                    <select className="ds-select" value={editForm.role}
                                        onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}>
                                        <option value="user">User</option>
                                        <option value="admin">Admin</option>
                                        <option value="editor">Editor</option>
                                        <option value="viewer">Viewer</option>
                                <option value="hr">HR</option>
                                <option value="sales">Sales</option>
                                <option value="management">Management</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <input type="checkbox" checked={editForm.is_active}
                                            onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })} />
                                        Attivo
                                    </label>
                                </div>
                                <div className="modal-actions">
                                    <button type="button" onClick={cancelEditing} className="ds-btn">Annulla</button>
                                    <button type="submit" className="ds-btn ds-btn-primary">Salva</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {loading ? (
                    <p style={{ color: 'var(--ds-txt-2)' }}>Caricamento...</p>
                ) : (
                    <div className="ds-table-wrap" style={{ overflowX: 'auto' }}>
                        <table className="ds-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Username</th>
                                    <th>Ruolo</th>
                                    <th>Stato</th>
                                    <th>Azioni</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsers.map(user => (
                                    <tr key={user.id}>
                                        <td>{user.id}</td>
                                        <td style={{ fontWeight: 500 }}>{user.username}</td>
                                        <td>{roleBadge(user.role)}</td>
                                        <td>
                                            <span className={`ds-badge ${user.is_active ? 'ds-badge-green' : 'ds-badge-gray'}`}>
                                                {user.is_active ? 'Attivo' : 'Disabilitato'}
                                            </span>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', gap: '6px' }}>
                                                <button onClick={() => handleImpersonate(user.username)} className="ds-btn" title="Impersonifica">👁️</button>
                                                <button onClick={() => startEditing(user)} className="ds-btn" title="Modifica">✏️</button>
                                                <button onClick={() => handleDeleteUser(user.id)} className="ds-btn ds-btn-danger" title="Elimina">🗑️</button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </main>
        </div>
    );
};

export default UserManager;
